import {Directive, forwardRef, Inject, InjectionToken, Input, OnDestroy, Self} from '@angular/core';
import {AbstractControl, NG_VALIDATORS, ValidationErrors, Validator} from '@angular/forms';
import {BehaviorSubject} from 'rxjs';
import {VALIDATION_CONTEXTS, ValidationContext, ValidationContextField} from '../../data/validation-context';
import {IncorrectValueRule, IValidationRuleError} from '../../rules/rules';
import {diff} from '~shared/utils/utils';

export const VALIDATION_CONTEXT_SEMANTIC = new InjectionToken<ValidationContext>('VALIDATION_CONTEXT_SEMANTIC');

export function validationContextSemanticFactory(): ValidationContext {
    return new ValidationContext('semantic');
}

export type SemanticValidator = ($data: object) => Record<string, ReadonlyArray<IValidationRuleError>> | null;

@Directive({
    // tslint:disable-next-line:directive-selector
    selector: '[semanticValidator]',
    providers: [
        {
            provide: VALIDATION_CONTEXT_SEMANTIC,
            useFactory: validationContextSemanticFactory,
        },
        {
            multi: true,
            provide: VALIDATION_CONTEXTS,
            useExisting: VALIDATION_CONTEXT_SEMANTIC,
        },
        {
            multi: true,
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => ValidationSemanticContextDirective),
        }
    ],
})
export class ValidationSemanticContextDirective implements Validator, OnDestroy {
    protected readonly validator$ = new BehaviorSubject<SemanticValidator>(undefined);

    protected previousValue: any;

    public constructor(
        @Self() @Inject(VALIDATION_CONTEXT_SEMANTIC)
        protected readonly validationContextSemantic: ValidationContext,
    ) {
    }

    public validate($c: AbstractControl): ValidationErrors | null {
        if (this.validator$.value) {
            // This comparison is required, since angular generates new object each time validity changes.
            // FIXME: This should be refactored.
            const difference = diff(this.previousValue, $c.value);
            if (difference && Object.keys(difference).length > 0) {
                this.previousValue = $c.value;
                const result = this.validator$.value($c.value);
                if (typeof result === 'object' && result !== null) {
                    const fields: Record<string, ValidationContextField> = {};
                    Object.keys(result)
                        .forEach($field => {
                            const field = new ValidationContextField($field);
                            field.swapRules(
                                result[$field].map($error => new IncorrectValueRule($error.value, $error.message))
                            );
                            fields[$field] = field;
                        });
                    this.validationContextSemantic.swapFields(fields);
                } else {
                    this.validationContextSemantic.clean();
                }
            }
        }
        return null;
    }

    public ngOnDestroy(): void {
        this.validator$.complete();
    }

    @Input()
    public set semanticValidator($value: SemanticValidator) {
        this.validator$.next($value);
    }
}
